import Decimal from "decimal.js";
import CREDIT_SCORE, { RANGE_560 } from "../constants/credit_score";
import { CREDIT_SCORE_VALUES } from "../mismo/constants";

// A plausible credit score. 0 / blank / typos are NOT valid scores, so they are
// ignored everywhere (a real FICO is ~300-850).
const MIN_VALID_SCORE = 300;
const MAX_VALID_SCORE = 850;
function validScore(value) {
  const num = parseInt(value, 10);
  return Number.isFinite(num) && num >= MIN_VALID_SCORE && num <= MAX_VALID_SCORE;
}

function getCreditScore(borrower) {
  return borrower.lowest_credit_mid_score
    ? new Decimal(borrower.lowest_credit_mid_score)
    : null;
}

export function lowest_credit_score(borrowers) {
  const lowestScore = borrowers
    .map(getCreditScore)
    .reduce((accum, creditScore) => {
      if (!accum) {
        return creditScore;
      }
      // We defined the "values" in the opposite order of the credit score amount.
      if (creditScore) {
        return accum.lt(creditScore) ? creditScore : accum;
      }
      return null;
    }, null);

  // Range 560 being the lowest defined right now.
  return lowestScore ? lowestScore : new Decimal(RANGE_560);
}

// Representative ("mid") credit score for one borrower, per mortgage
// convention:
//   - 3 or more scores: the median (the middle value when sorted).
//   - exactly 2 scores: the LOWER of the two.
//   - 1 score: that score.
// Example: 741, 760, 787 -> 760. Returns a number, or null when no valid
// numeric scores are present.
export function representative_mid_score(scores) {
  const nums = (scores || [])
    .filter(validScore)
    .map(score => parseInt(score, 10))
    .sort((a, b) => a - b);

  if (nums.length === 0) {
    return null;
  }
  if (nums.length === 1) {
    return nums[0];
  }
  if (nums.length === 2) {
    return nums[0];
  }
  return nums[Math.floor(nums.length / 2)];
}

// Map a numeric mid score to its range-bucket id (the same buckets pricing
// uses). Returns "" when the score isn't a valid number.
export function range_id_for_mid_score(mid) {
  const value = parseInt(mid, 10);
  if (!Number.isFinite(value)) {
    return "";
  }
  const scores = [...Object.entries(CREDIT_SCORE_VALUES)].sort(
    ([, minScoreA], [, minScoreB]) => minScoreA - minScoreB
  );
  let rangeId = "";
  for (const [id, minScore] of scores) {
    if (value < minScore) {
      break;
    }
    rangeId = id;
  }
  return rangeId;
}

// The lowest representative (mid) score across all borrowers, as a number.
// Drives the Summary Sheet "Credit Score" cell. Returns null when no borrower
// has an imported numeric mid score (fall back to the range bucket display).
export function lowest_mid_score_numeric(borrowers) {
  const mids = (borrowers || [])
    .filter(borrower => borrower && validScore(borrower.credit_mid_score))
    .map(borrower => parseInt(borrower.credit_mid_score, 10));

  return mids.length ? Math.min(...mids) : null;
}

// True when a borrower has a usable credit score for pricing — EITHER a valid
// numeric Mid Score OR a chosen range bucket. Used to gate the Save button.
export function borrower_has_credit_score(borrower) {
  if (!borrower) {
    return false;
  }
  const hasRange =
    borrower.lowest_credit_mid_score !== "" &&
    borrower.lowest_credit_mid_score !== undefined &&
    borrower.lowest_credit_mid_score !== null;
  return validScore(borrower.credit_mid_score) || hasRange;
}

// One place that decides what the "Credit Score" cell shows on the Summary
// Sheet and the LE Price Engine — the lowest numeric Mid Score across borrowers
// when available, otherwise the lowest range-bucket label. "" when neither.
export function credit_score_display(borrowers) {
  const mid = lowest_mid_score_numeric(borrowers);
  if (mid != null) {
    return String(mid);
  }
  const anyRange = (borrowers || []).some(
    borrower => borrower && borrower.lowest_credit_mid_score
  );
  if (anyRange) {
    return CREDIT_SCORE[lowest_credit_score(borrowers).toString()] || "";
  }
  return "";
}
